// react
import React from 'react';
import styled from 'styled-components';

// radix
import * as RadixAccordion from '@radix-ui/react-accordion';

// components
import { Icon, type IconSize } from '@/components/atoms/Icon/Icon';
import type { Icons } from '@/components/atoms/Icon/icons';

// theme
import { Color } from '@/theme/color';

/**
 * Accordion is a collapsible accordion.
 */
export const Accordion = ({
  items = [],
  defaultValue,
  titleSize = 'lg',
  iconSize = 'sm',
}: AccordionProps) => {
  return (
    <AccordionRoot
      defaultValue={defaultValue ? [defaultValue] : undefined}
      type='multiple'
    >
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          <AccordionItem key={item.label} value={item.id}>
            {/* header */}
            <AccordionHeader>
              {item.icon && (
                <StyledIconContainer
                  $primaryColor={item.primaryColor}
                  $secondaryColor={item.secondaryColor}
                >
                  <Icon icon={item.icon} size={iconSize} fontSize={14} />
                </StyledIconContainer>
              )}
              <AccordionTrigger icon={item.icon} $fontsize={titleSize}>
                <AccordionTitle>{item.label}</AccordionTitle>
                <AccordionChevron className='chevron'>
                  <Icon icon='chevron-down' fontSize={16} />
                </AccordionChevron>
              </AccordionTrigger>
            </AccordionHeader>

            {/* content */}
            <AccordionContent>
              <AccordionContentText>{item.content}</AccordionContentText>
            </AccordionContent>
          </AccordionItem>
          {i < items.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </AccordionRoot>
  );
};

const AccordionRoot = styled(RadixAccordion.Root)``;

const AccordionItem = styled(RadixAccordion.Item)`
  margin-top: 1px;
  &:focus-within {
    position: relative;
    z-index: 1;
  }
`;

const AccordionHeader = styled(RadixAccordion.Header)`
  display: flex;
  align-items: center;
  padding-top: ${(props) => props.theme.spacing[12]};
`;

const AccordionChevron = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.color.neutral[90]};
  transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
`;

const AccordionTrigger = styled(RadixAccordion.Trigger)<{
  icon?: string;
  $fontsize?: Size;
}>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font: ${(props) =>
    props.$fontsize === 'lg'
      ? props.theme.typography.body.xl.semibold
      : props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
  flex: 1;
  cursor: pointer;
  padding: 0 ${(props) => props.theme.spacing[4]};
  margin-left: ${(props) => (props.icon ? props.theme.spacing[12] : '0')};

  &[data-state='open'] > .chevron {
    transform: rotate(180deg);
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const AccordionContent = styled(RadixAccordion.Content)`
  overflow: hidden;
  &[data-state='open'] {
    animation: slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  &[data-state='closed'] {
    animation: slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  @keyframes slideDown {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes slideUp {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  }
`;

const AccordionContentText = styled.div`
  padding: ${(props) => props.theme.spacing[12]};
`;

const Divider = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[50]};
  margin-bottom: ${(props) => props.theme.spacing[12]};
  padding-top: ${(props) => props.theme.spacing[12]};
  width: 100%;
`;

const StyledIconContainer = styled.div<{
  $primaryColor?: Color;
  $secondaryColor?: Color;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing[4]};
  border-radius: 99999px;
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;

  background-color: ${(props) =>
    props.$secondaryColor || props.theme.color.neutral[40]};
  color: ${(props) => props.$primaryColor || props.theme.color.neutral[10]};
`;

type Size = 'md' | 'lg';

export interface Item {
  id: string;
  label: string;
  content: React.ReactNode;
  isComplete?: boolean;
  icon?: Icons;
  primaryColor?: Color;
  secondaryColor?: Color;
}

interface AccordionProps {
  items?: Item[];
  defaultValue?: string;
  titleSize?: Size;
  iconSize?: IconSize;
}

Accordion.displayName = 'Accordion';

export default Accordion;
